/**
 * La liste d'employés vue du conseiller — chunk 9.BK.1.
 *
 * ## Ce que ce panneau fait, et ne fait pas
 *
 * Il montre **le même census** que le client remplit — pas une copie, pas une
 * voie parallèle. Le conseiller peut corriger tant que la liste n'est pas
 * verrouillée, puis **verrouiller** : c'est ce geste, et lui seul, qui satisfait
 * le requis et débloque l'envoi aux assureurs.
 *
 * Il ne recalcule **aucune** règle. Le statut, les manques et l'écart d'effectif
 * arrivent du serveur ; l'écran les met en forme.
 *
 * ## L'écart d'effectif est une information, pas une alerte
 *
 * Déclaré contre constaté, en gris, sans icône d'avertissement et sans jamais
 * empêcher quoi que ce soit. Un employeur a mille raisons légitimes d'un écart —
 * temps partiel, départs, admissibilité — et le teinter en rouge ferait chercher
 * un défaut qui n'existe pas.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useCensusActions,
  useProjectCensus,
  type BrokerCensus,
  type CensusParticipantRow,
} from "../../../lib/queries/census";

import { ImportCensusDialog } from "./ImportCensusDialog";

export function CensusSection({ projectId }: { readonly projectId: string }): JSX.Element | null {
  const { t } = useTranslation();
  const { data: census, isPending } = useProjectCensus(projectId);
  const actions = useCensusActions(projectId);
  const [dialogueOuvert, setDialogueOuvert] = useState(false);
  const [remiseOuverte, setRemiseOuverte] = useState(false);
  const [importOuvert, setImportOuvert] = useState(false);
  const [declarationOuverte, setDeclarationOuverte] = useState(false);
  const [importFait, setImportFait] = useState<{
    rows_applied: number;
    rows_replaced: number;
  } | null>(null);
  const [lien, setLien] = useState<string | null>(null);

  if (isPending) return null;

  if (!census) {
    return (
      <Cadre>
        <p className="text-sm text-slate-600">{t("census.broker.none")}</p>
        <button
          type="button"
          onClick={() => actions.creer.mutate()}
          disabled={actions.creer.isPending}
          className="focus-visible:ring-brand-500 mt-3 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60"
        >
          {t("census.broker.create")}
        </button>
      </Cadre>
    );
  }

  // Même raison : un statut manquant ne doit pas faire planter la page.
  if (!census.status) return null;
  const verrouille = census.status === "locked";
  const peutVerrouiller = census.status === "submitted" && census.issues.length === 0;

  return (
    <Cadre>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{t("census.broker.title")}</h3>
          <p className="mt-0.5 text-xs text-slate-500">{t(cleDeStatut(census))}</p>
        </div>
        <Effectif census={census} />
      </div>

      <Manques census={census} />

      <TableauEmployes census={census} />

      {/* L'import du classeur — en saisie SEULEMENT.
          Un import remplace l'effectif : l'offrir après que le client a déclaré
          sa liste terminée changerait sous lui ce qu'il a arrêté. Le chemin
          existe et il est tracé — déverrouiller, puis remettre en saisie. */}
      {census.status === "draft" ? (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setImportOuvert(true)}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("census.import.open")}
          </button>
          {importFait ? (
            <p role="status" className="mt-2 text-xs text-emerald-700">
              {t("census.import.done", {
                count: importFait.rows_applied,
                replaced: importFait.rows_replaced,
              })}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Le lien vers la liste, pour le client. Créer et ENVOYER restent deux
          gestes : celui-ci ne fait partir aucun courriel. */}
      {verrouille ? null : (
        <div className="mt-4">
          <button
            type="button"
            onClick={() =>
              actions.creerLien.mutate(census.id, { onSuccess: (r) => setLien(r.census_url) })
            }
            disabled={actions.creerLien.isPending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60"
          >
            {t("census.broker.make_link")}
          </button>
          {lien ? (
            <div role="status" className="mt-2 rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-600">{t("census.broker.link_ready")}</p>
              {/* Lisible et sélectionnable. Un lien qu'on ne peut que « copier »
                  par un bouton est perdu si le presse-papiers échoue. */}
              <code className="mt-1 block break-all text-xs text-slate-900">{lien}</code>
              <p className="mt-1 text-xs text-slate-400">{t("census.broker.link_once")}</p>
            </div>
          ) : null}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        {/* Déclarer la collecte terminée à la place du client — en SAISIE
            seulement. Le flux réel : le client renvoie son classeur par
            courriel, le conseiller l'importe ; l'obliger ensuite à ouvrir un
            lien juste pour cliquer « J'ai terminé » serait une cérémonie sans
            objet. La revue reste à faire : ce geste ne verrouille rien. */}
        {census.status === "draft" ? (
          <>
            <button
              type="button"
              onClick={() => setDeclarationOuverte(true)}
              disabled={census.participants.length === 0}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("census.broker.submit_for_client")}
            </button>
            <p className="text-xs text-slate-500">{t("census.broker.submit_hint")}</p>
          </>
        ) : null}

        {verrouille ? (
          <button
            type="button"
            onClick={() => setDialogueOuvert(true)}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("census.broker.unlock")}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => actions.verrouiller.mutate(census.id)}
              disabled={!peutVerrouiller || actions.verrouiller.isPending}
              className="focus-visible:ring-brand-500 bg-brand-600 hover:bg-brand-700 touch-manipulation rounded-lg px-3 py-1.5 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {actions.verrouiller.isPending ? t("census.broker.locking") : t("census.broker.lock")}
            </button>
            <p className="text-xs text-slate-500">
              {/* Dire POURQUOI le bouton est éteint. Un bouton grisé sans raison
                  est une impasse : la personne ne sait pas quoi faire ensuite. */}
              {census.status !== "submitted"
                ? t("census.broker.lock_blocked")
                : t("census.broker.lock_hint")}
            </p>
            {/* Mini-fix 9.BK.3 — la sortie de secours qui n'existait pas. Un
                effectif déclaré terminé n'était plus modifiable par aucun écran :
                ni import, ni page client, ni édition. Le geste est offert ICI,
                sur l'état `submitted`, et jamais depuis le verrou — un effectif
                verrouillé se déverrouille d'abord. */}
            {census.status === "submitted" ? (
              <>
                <button
                  type="button"
                  onClick={() => setRemiseOuverte(true)}
                  className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
                >
                  {t("census.broker.reopen")}
                </button>
                <p className="text-xs text-slate-500">{t("census.broker.reopen_hint")}</p>
              </>
            ) : null}
          </>
        )}
      </div>

      {declarationOuverte ? (
        <DialogueConfirmation
          titre={t("census.broker.submit_title")}
          corps={t("census.broker.submit_body")}
          confirmer={t("census.broker.submit_confirm")}
          enCours={actions.declarerTermine.isPending}
          onAnnuler={() => setDeclarationOuverte(false)}
          onConfirmer={() =>
            actions.declarerTermine.mutate(census.id, {
              onSuccess: () => setDeclarationOuverte(false),
            })
          }
        />
      ) : null}

      {importOuvert ? (
        <ImportCensusDialog
          projectId={projectId}
          censusId={census.id}
          onFermer={() => setImportOuvert(false)}
          onImporte={(comptes) => {
            setImportFait(comptes);
            setImportOuvert(false);
          }}
        />
      ) : null}

      {dialogueOuvert ? (
        <DialogueMotif
          cle="census-unlock"
          titre={t("census.broker.unlock_title")}
          corps={t("census.broker.unlock_body")}
          etiquette={t("census.broker.unlock_reason")}
          aide={t("census.broker.unlock_reason_required")}
          confirmer={t("census.broker.unlock_confirm")}
          onAnnuler={() => setDialogueOuvert(false)}
          onConfirmer={(reason) => {
            actions.deverrouiller.mutate(
              { censusId: census.id, reason },
              { onSuccess: () => setDialogueOuvert(false) },
            );
          }}
          enCours={actions.deverrouiller.isPending}
        />
      ) : null}

      {remiseOuverte ? (
        <DialogueMotif
          cle="census-reopen"
          titre={t("census.broker.reopen_title")}
          corps={t("census.broker.reopen_body")}
          etiquette={t("census.broker.reopen_reason")}
          aide={t("census.broker.reopen_reason_required")}
          confirmer={t("census.broker.reopen_confirm")}
          onAnnuler={() => setRemiseOuverte(false)}
          onConfirmer={(reason) => {
            actions.remettreEnSaisie.mutate(
              { censusId: census.id, reason },
              { onSuccess: () => setRemiseOuverte(false) },
            );
          }}
          enCours={actions.remettreEnSaisie.isPending}
        />
      ) : null}
    </Cadre>
  );
}

/**
 * Le libellé du statut — **qui dit la vérité sur l'auteur**.
 *
 * `submitted` ne dit plus « le client a fini » : le cabinet peut déclarer la
 * collecte terminée quand le client a renvoyé son classeur par courriel.
 * Afficher « Terminée par le client » dans ce cas serait un mensonge à l'écran,
 * et le conseiller qui relit six mois plus tard n'aurait aucun moyen de le
 * savoir.
 *
 * Le repli sur le libellé « client » n'existe que pour les census déclarés
 * avant que la provenance existe — la migration les a d'ailleurs marqués
 * `client`, puisque c'était le seul chemin branché.
 */
function cleDeStatut(census: BrokerCensus): string {
  if (census.status !== "submitted") return `census.broker.status_${census.status}`;
  return census.submitted_source === "user"
    ? "census.broker.status_submitted_user"
    : "census.broker.status_submitted_client";
}

function Cadre({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      {children}
    </section>
  );
}

/**
 * L'écart déclaré / constaté.
 *
 * Neutre par construction : même couleur que le reste du texte secondaire, et
 * une note qui dit explicitement qu'il ne bloque rien. Il n'y a d'ailleurs aucun
 * chemin par lequel il pourrait bloquer — cf. `send-readiness.ts`.
 */
function Effectif({ census }: { census: BrokerCensus }): JSX.Element {
  const { t } = useTranslation();
  // Repli défensif. Ce panneau vit sur la page principale du dossier : une
  // réponse d'une forme inattendue doit lui coûter SON affichage, pas celui de
  // toute la page. Le compte se relit d'ailleurs des lignes déjà reçues.
  const { declared, counted } = census.headcount ?? {
    declared: null,
    counted: census.participants?.length ?? 0,
  };
  return (
    <div className="text-right">
      <p className="text-xs tabular-nums text-slate-600">
        {declared === null
          ? t("census.broker.headcount_unknown", { counted })
          : t("census.broker.headcount", { counted, declared })}
      </p>
      <p className="text-xs text-slate-400">{t("census.broker.headcount_note")}</p>
    </div>
  );
}

function Manques({ census }: { census: BrokerCensus }): JSX.Element {
  const { t } = useTranslation();
  if (census.issues.length === 0) {
    const message =
      census.status === "locked"
        ? "census.broker.already_locked"
        : census.participants.length === 0
          ? "census.broker.empty_list"
          : census.status === "draft"
            ? "census.broker.draft_complete"
            : "census.broker.ready";
    return <p className="mt-3 text-xs text-slate-600">{t(message)}</p>;
  }
  return (
    <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      {t("census.broker.issues_title", { count: census.issues.length })}
    </p>
  );
}

/**
 * La valeur brute conservée pour un champ, si l'analyse ne l'a pas su lire.
 *
 * Rendue **en ambre** par l'appelant : ce n'est pas une donnée valide, c'est une
 * décision en attente. Elle disparaît d'elle-même dès que le champ est corrigé,
 * puisque le serveur retire alors la clé.
 */
function brutDe(p: CensusParticipantRow, champ: string): JSX.Element | null {
  const v = p.import_raw?.[champ];
  if (typeof v !== "string" || v.length === 0) return null;
  return <span className="text-amber-700">{v}</span>;
}

function TableauEmployes({ census }: { census: BrokerCensus }): JSX.Element | null {
  const { t } = useTranslation();
  if (census.participants.length === 0) return null;

  const manquesDe = (rowId: string) =>
    census.issues.filter((i) => i.client_row_id === rowId).length;
  const pacDe = (rowId: string) =>
    census.dependents.filter((d) => d.participant_client_row_id === rowId).length;

  return (
    // Un tableau dense pour la revue — le conseiller est sur un écran, pas sur
    // un téléphone. Le défilement horizontal reste DANS ce conteneur : la page
    // elle-même ne défile jamais latéralement.
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[42rem] text-left text-xs">
        <thead className="text-slate-500">
          <tr>
            <th scope="col" className="py-1.5 pr-3 font-medium">
              {t("census.broker.table_number")}
            </th>
            <th scope="col" className="py-1.5 pr-3 font-medium">
              {t("census.broker.table_employee")}
            </th>
            <th scope="col" className="py-1.5 pr-3 font-medium">
              {t("census.broker.table_birth")}
            </th>
            <th scope="col" className="py-1.5 pr-3 font-medium">
              {t("census.broker.table_coverage")}
            </th>
            <th scope="col" className="py-1.5 pr-3 font-medium">
              {t("census.broker.table_pay")}
            </th>
            <th scope="col" className="py-1.5 font-medium">
              {t("census.broker.table_issues")}
            </th>
          </tr>
        </thead>
        <tbody className="text-slate-800">
          {census.participants.map((p) => {
            const n = manquesDe(p.client_row_id);
            const pac = pacDe(p.client_row_id);
            return (
              <tr key={p.client_row_id} className="border-t border-slate-100">
                <td className="py-1.5 pr-3 tabular-nums">{p.employee_number ?? "—"}</td>
                <td className="py-1.5 pr-3">
                  <span className="break-words">
                    {[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}
                  </span>
                  {pac > 0 ? (
                    <span className="ml-1 text-slate-400">
                      · {t("census.broker.dependents_count", { count: pac })}
                    </span>
                  ) : null}
                </td>
                <td className="py-1.5 pr-3 tabular-nums">
                  {/* Ce que le classeur disait, tant que personne n'a tranché.
                      Sans cela, le conseiller lit « — » et doit rouvrir le
                      fichier pour retrouver la ligne parmi deux cents. */}
                  {p.birth_date ?? brutDe(p, "birth_date") ?? "—"}
                </td>
                <td className="py-1.5 pr-3">{p.coverage ?? "—"}</td>
                <td className="py-1.5 pr-3 tabular-nums">
                  {p.annual_salary ?? (p.hourly_rate ? `${p.hourly_rate} / h` : "—")}
                </td>
                <td
                  className={`py-1.5 tabular-nums ${n > 0 ? "text-amber-700" : "text-slate-400"}`}
                >
                  {n > 0 ? n : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Le déverrouillage demande une raison, et le dit avant d'agir.
 *
 * Il ramène à `submitted`, **jamais** à `draft` : le client avait déclaré sa
 * saisie terminée, et ce fait ne s'efface pas parce que le cabinet corrige.
 */
/**
 * Une confirmation avant un geste qui change l'état de la liste.
 *
 * Déclarer la collecte terminée ferme l'écriture au client et engage le nom du
 * conseiller comme auteur : ce n'est pas un geste qu'on fait en passant.
 * Le dialogue dit **ce qui va changer**, pas seulement « êtes-vous sûr ».
 */
function DialogueConfirmation({
  titre,
  corps,
  confirmer,
  enCours,
  onAnnuler,
  onConfirmer,
}: {
  titre: string;
  corps: string;
  confirmer: string;
  enCours: boolean;
  onAnnuler: () => void;
  onConfirmer: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="census-confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center overscroll-contain bg-slate-900/40 p-4"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <h4 id="census-confirm-title" className="text-sm font-semibold text-slate-900">
          {titre}
        </h4>
        <p className="mt-2 text-sm text-slate-600">{corps}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onAnnuler}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("census.broker.cancel")}
          </button>
          <button
            type="button"
            autoFocus
            disabled={enCours}
            onClick={onConfirmer}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {enCours ? t("census.broker.locking") : confirmer}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Un geste qui annule quelque chose, et qui doit dire pourquoi.
 *
 * Généralisé au mini-fix 9.BK.3 : le déverrouillage et la remise en saisie sont
 * **deux décisions distinctes**, mais elles demandent la même chose — une
 * confirmation et un motif non vide. Dupliquer soixante-dix lignes de balisage
 * aurait fait diverger l'accessibilité des deux au premier ajustement.
 *
 * Les textes sont passés en propriétés plutôt que déduits d'un drapeau : un
 * booléen `estRemiseEnSaisie` rendrait le composant illisible dès le troisième
 * usage.
 */
function DialogueMotif({
  cle,
  titre,
  corps,
  etiquette,
  aide,
  confirmer,
  onAnnuler,
  onConfirmer,
  enCours,
}: {
  /** Préfixe d'identifiant — deux dialogues ne partagent jamais un `id`. */
  cle: string;
  titre: string;
  corps: string;
  etiquette: string;
  aide: string;
  confirmer: string;
  onAnnuler: () => void;
  onConfirmer: (reason: string) => void;
  enCours: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [raison, setRaison] = useState("");
  const vide = raison.trim().length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${cle}-title`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <h4 id={`${cle}-title`} className="text-sm font-semibold text-slate-900">
          {titre}
        </h4>
        <p className="mt-2 text-sm text-slate-600">{corps}</p>

        <label htmlFor={`${cle}-reason`} className="mt-4 block text-xs font-medium text-slate-600">
          {etiquette}
        </label>
        <textarea
          id={`${cle}-reason`}
          rows={3}
          value={raison}
          autoFocus
          onChange={(e) => setRaison(e.target.value)}
          aria-invalid={vide ? true : undefined}
          className="focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
        {vide ? <p className="mt-1 text-xs text-slate-500">{aide}</p> : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onAnnuler}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("census.broker.cancel")}
          </button>
          <button
            type="button"
            disabled={vide || enCours}
            onClick={() => onConfirmer(raison)}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {confirmer}
          </button>
        </div>
      </div>
    </div>
  );
}
